// src/components/quiz/QuizForm.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  IconButton,
  Button,
  Stack,
  Tooltip,
} from '@mui/material';
import { ReactComponent as CoinIcon } from '../../assets/icons/coin.svg';
import DeleteIcon from '@mui/icons-material/Delete';
import RequestService from '../../services/RequestService';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

const QuizForm = ({
  formData,
  setFormData,
  activeStep,
  user,
  games = [],
  groups = [],
}) => {
  const [iaConfigOpen, setIaConfigOpen] = useState(false);
  const [iaFields, setIaFields] = useState({
    topic: '',
    language: 'pt-BR',
    difficulty: 'easy',
    number_of_questions: '1',
  });
  const [loadingIa, setLoadingIa] = useState(false);

  const handleAddQuestion = () => {
    setFormData((prev) => ({
      ...prev,
      questions: [
        ...prev.questions,
        { title: '', coin: 0, position: prev.questions.length, options: [] },
      ],
    }));
  };

  const handleAddOption = (qIndex) => {
    const updated = [...formData.questions];
    updated[qIndex].options.push({ text: '', correct: false });
    setFormData({ ...formData, questions: updated });
  };

  const calculateXP = (difficulty) => {
    switch (difficulty) {
      case 'EASY':
        return 100;
      case 'INTERMEDIATE':
        return 200;
      case 'HARD':
        return 300;
      default:
        return 0;
    }
  };

  const totalCoins = useMemo(
    () =>
      (formData.questions || []).reduce(
        (sum, q) => sum + (Number(q.coin) || 0),
        0
      ),
    [formData.questions]
  );

  const reindexQuestions = (questions) =>
    questions.map((q, idx) => ({ ...q, position: idx }));

  const handleDeleteQuestion = (qIndex) => {
    setFormData((prev) => {
      const next = [...prev.questions];
      next.splice(qIndex, 1);

      const reindexed = reindexQuestions(next);

      return {
        ...prev,
        questions: reindexed,
      };
    });
  };

  switch (activeStep) {
    case 0:
      return (
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Título"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            fullWidth
          />
          <TextField
            label="Descrição"
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
            fullWidth
            multiline
          />
          {/* Dificuldade */}
          <Stack direction="column" spacing={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography>Classificação do Quiz</Typography>
              <Tooltip title="A classificação afeta na quantidade de experiência recebida pelo jogador. Sendo 100, 200 e 300 de experiência, respectivamente.">
                <HelpOutlineIcon
                  color="primary"
                  sx={{
                    width: '18px',
                    height: '18px',
                  }}
                />
              </Tooltip>
            </Stack>
            <ToggleButtonGroup
              value={formData.difficulty}
              exclusive
              fullWidth
              onChange={(e, value) => {
                if (value !== null) {
                  const xpByDifficulty = {
                    EASY: 100,
                    INTERMEDIATE: 200,
                    HARD: 300,
                  };
                  setFormData((prev) => ({
                    ...prev,
                    difficulty: value,
                    xp: xpByDifficulty[value] || 0,
                  }));
                }
              }}
              color="primary"
            >
              <ToggleButton value="EASY">Comum</ToggleButton>
              <ToggleButton value="INTERMEDIATE">Especial</ToggleButton>
              <ToggleButton value="HARD">Prioritária</ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Tentativas máximas"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={formData.maxAttempts}
              onChange={(e) => {
                const val = e.target.value;
                const parsed = parseInt(val, 10);
                setFormData({
                  ...formData,
                  maxAttempts: isNaN(parsed) ? 0 : parsed,
                });
              }}
            />
            <TextField
              label="Tempo por pergunta (segundos)"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
              value={formData.secondsPerQuestion}
              onChange={(e) => {
                const val = e.target.value;
                const parsed = parseInt(val, 10);
                setFormData({
                  ...formData,
                  secondsPerQuestion: isNaN(parsed) ? 0 : parsed,
                });
              }}
            />
          </Stack>

          <ToggleButtonGroup
            fullWidth
            value={formData.isLimitedTime}
            exclusive
            onChange={(e, value) => {
              if (value !== null) {
                setFormData({ ...formData, isLimitedTime: value });
              }
            }}
          >
            <ToggleButton value={false}>Sem data</ToggleButton>
            <ToggleButton value={true}>Com data</ToggleButton>
          </ToggleButtonGroup>
          {formData.isLimitedTime && (
            <>
              <TextField
                type="datetime-local"
                label="Início"
                InputLabelProps={{ shrink: true }}
                value={formData.startDate}
                onChange={(e) =>
                  setFormData({ ...formData, startDate: e.target.value })
                }
              />
              <TextField
                type="datetime-local"
                label="Fim"
                InputLabelProps={{ shrink: true }}
                value={formData.endDate}
                onChange={(e) =>
                  setFormData({ ...formData, endDate: e.target.value })
                }
              />
            </>
          )}
        </Box>
      );

    case 1:
      return (
        <Box display="flex" flexDirection="column" gap={2}>
          <Typography variant="body1">
            Esse quiz será disponibilizado para:
          </Typography>
          <ToggleButtonGroup
            value={formData.entireGame}
            exclusive
            fullWidth
            onChange={(e, value) => {
              if (value !== null) {
                setFormData((prev) => ({
                  ...prev,
                  entireGame: value,
                  gameIds: [],
                  groupIds: [],
                }));
              }
            }}
          >
            <ToggleButton value={true}>Jogos Selecionados</ToggleButton>
            <ToggleButton value={false}>Grupos Específicos</ToggleButton>
          </ToggleButtonGroup>

          {formData.entireGame ? (
            <FormControl fullWidth>
              <InputLabel>Jogos</InputLabel>
              <Select
                multiple
                value={formData.gameIds}
                onChange={(e) =>
                  setFormData({ ...formData, gameIds: e.target.value })
                }
              >
                {games.map((game) => (
                  <MenuItem key={game.id} value={game.id}>
                    {game.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <FormControl fullWidth>
              <InputLabel>Grupos</InputLabel>
              <Select
                multiple
                value={formData.groupIds}
                onChange={(e) =>
                  setFormData({ ...formData, groupIds: e.target.value })
                }
              >
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>
      );

    case 2:
      return (
        <Box display="flex" flexDirection="column" gap={2}>
          <Button
            variant="outlined"
            endIcon={<AutoFixHighIcon />}
            onClick={() => setIaConfigOpen(!iaConfigOpen)}
          >
            {iaConfigOpen ? 'Desabilitar geração com a Gio' : 'Gerar com a Gio'}
          </Button>
          {iaConfigOpen && (
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Tema"
                value={iaFields.topic}
                onChange={(e) =>
                  setIaFields({ ...iaFields, topic: e.target.value })
                }
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Idioma</InputLabel>
                <Select
                  value={iaFields.language}
                  onChange={(e) =>
                    setIaFields((prev) => ({
                      ...prev,
                      language: e.target.value,
                    }))
                  }
                  label="Idioma"
                >
                  <MenuItem value="pt-BR">Português</MenuItem>
                  <MenuItem value="en-US">Inglês</MenuItem>
                  <MenuItem value="it">Italiano</MenuItem>
                  <MenuItem value="de">Alemão</MenuItem>
                  <MenuItem value="fr">Francês</MenuItem>
                  <MenuItem value="es">Espanhol</MenuItem>
                </Select>
              </FormControl>
              <Stack direction="row" alignItems="center" gap={1}>
                <Typography>Dificuldade do Quiz</Typography>
                <Tooltip title="O nível de dificuldade irá interferir, diretamente, na dificuldade de cada questão gerada">
                  <HelpOutlineIcon
                    color="primary"
                    sx={{
                      width: '18px',
                      height: '18px',
                    }}
                  />
                </Tooltip>
              </Stack>
              <ToggleButtonGroup
                value={iaFields.difficulty}
                exclusive
                fullWidth
                onChange={(e, value) => {
                  if (value)
                    setIaFields((prev) => ({ ...prev, difficulty: value }));
                }}
                color="primary"
              >
                <ToggleButton value="easy">Fácil</ToggleButton>
                <ToggleButton value="medium">Normal</ToggleButton>
                <ToggleButton value="hard">Difícil</ToggleButton>
              </ToggleButtonGroup>

              {/* <TextField
                label="Número de perguntas"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  min: 1,
                  max: 10,
                }}
                value={iaFields.number_of_questions}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = parseInt(val, 10);
                  const clamped = Math.max(
                    1,
                    Math.min(10, isNaN(parsed) ? 1 : parsed)
                  );
                  setIaFields({
                    ...iaFields,
                    number_of_questions: clamped,
                  });
                }}
              /> */}

              <TextField
                label="Número de perguntas"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={iaFields.number_of_questions}
                onChange={(e) => {
                  const val = e.target.value;

                  if (val === '') {
                    setIaFields((prev) => ({
                      ...prev,
                      number_of_questions: '',
                    }));
                    return;
                  }

                  if (!/^\d+$/.test(val)) return;

                  setIaFields((prev) => ({
                    ...prev,
                    number_of_questions: val,
                  }));
                }}
                onBlur={() => {
                  const n = parseInt(iaFields.number_of_questions || '0', 10);
                  const clamped = Math.min(10, Math.max(1, isNaN(n) ? 1 : n));
                  setIaFields((prev) => ({
                    ...prev,
                    number_of_questions: String(clamped),
                  }));
                }}
              />

              <Button
                variant="contained"
                onClick={async () => {
                  setLoadingIa(true);
                  try {
                    const response = await RequestService.generateQuizFromIa({
                      topic: iaFields.topic,
                      companyId: user.company.id,
                      difficulty: iaFields.difficulty,
                      language: iaFields.language,
                      number_of_questions: parseInt(
                        iaFields.number_of_questions,
                        10
                      ),
                    });

                    const updated = response.map((q, i) => ({
                      ...q,
                      position: i,
                    }));

                    setFormData((prev) => ({
                      ...prev,
                      questions: updated,
                    }));
                  } catch (e) {
                    console.error(e);
                    alert('Erro ao gerar perguntas com IA.');
                  } finally {
                    setLoadingIa(false);
                  }
                }}
                disabled={loadingIa}
              >
                {loadingIa ? 'Gerando...' : 'Gerar Perguntas com IA'}
              </Button>
            </Box>
          )}

          {formData.questions.map((q, i) => (
            <Box key={i} border="1px solid #ccc" p={2}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <TextField
                  label={`Pergunta ${i + 1}`}
                  value={q.title}
                  onChange={(e) => {
                    const updated = [...formData.questions];
                    updated[i].title = e.target.value;
                    setFormData({ ...formData, questions: updated });
                  }}
                  fullWidth
                />
                <Stack
                  direction="row"
                  alignItems="center"
                  gap={1}
                  sx={{ minWidth: 130 }}
                >
                  <CoinIcon />
                  <TextField
                    label="Moedas"
                    inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                    value={formData.questions[i].coin}
                    onChange={(e) => {
                      const updated = [...formData.questions];
                      const val = e.target.value;
                      const parsed = parseInt(val, 10);
                      updated[i].coin = isNaN(parsed) ? 0 : parsed;
                      setFormData({ ...formData, questions: updated });
                    }}
                  />
                  <IconButton onClick={() => handleDeleteQuestion(i)}>
                    <DeleteIcon sx={{ color: 'red' }} />
                  </IconButton>
                </Stack>
              </Stack>

              {q.options.map((opt, j) => (
                <Box key={j} display="flex" alignItems="center" gap={1} mt={1}>
                  <Checkbox
                    checked={opt.correct}
                    onChange={(e) => {
                      const updated = [...formData.questions];
                      updated[i].options[j].correct = e.target.checked;
                      setFormData({ ...formData, questions: updated });
                    }}
                  />
                  <TextField
                    label={`Opção ${j + 1}`}
                    value={opt.text}
                    onChange={(e) => {
                      const updated = [...formData.questions];
                      updated[i].options[j].text = e.target.value;
                      setFormData({ ...formData, questions: updated });
                    }}
                  />
                  <IconButton
                    onClick={() => {
                      const updated = [...formData.questions];
                      updated[i].options.splice(j, 1);
                      setFormData({ ...formData, questions: updated });
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}
              <Button size="small" onClick={() => handleAddOption(i)}>
                Adicionar Opção
              </Button>
            </Box>
          ))}
          <Button onClick={handleAddQuestion}>Adicionar Pergunta</Button>
        </Box>
      );

    case 3:
      return (
        <Box>
          <Typography>
            <strong>Título:</strong> {formData.title}
          </Typography>
          <Typography>
            <strong>Descrição:</strong> {formData.description}
          </Typography>
          <Typography>
            <strong>XP:</strong> {formData.xp}
          </Typography>
          <Typography>
            <strong>Moedas:</strong> {totalCoins}
          </Typography>
          <Typography>
            <strong>Qtd. de Perguntas:</strong> {formData.questions.length}
          </Typography>
        </Box>
      );

    default:
      return null;
  }
};

export default QuizForm;
